import resolveRulebookListCardStatus from "@next-platform/client-common-core/rulebook/cards/resolveRulebookListCardStatus";
import { RulebookListCardStatus } from "@next-platform/client-common-core/rulebook/cards/RulebookListCardStatus";
import type { RulebookDraft } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import {
  deriveRulebookSummaryFields,
  type RulebookMeterChip,
  type RulebookModerationFix,
  type RulebookSummary,
} from "@next-platform/packages-ts-types";

import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

export type RulebookListCardFields = {
  name: string;
  description: string;
  coverUrl?: string;
  meterChips: RulebookMeterChip[];
  numHiddenMeters: number;
  numRules: number;
  numBots: number;
  numPersonas: number;
  status: RulebookListCardStatus;
  moderationFix?: RulebookModerationFix;
};

type DerivedRulebookFields = ReturnType<typeof deriveRulebookSummaryFields> | undefined;

const getDraftRulebookFields = (draft?: RulebookDraft): DerivedRulebookFields => (draft ? deriveRulebookSummaryFields(draft.definition) : undefined);

const getRulebookCoverUrl = ({ summary, draft }: { summary?: RulebookSummary; draft?: RulebookDraft }) =>
  getAvatarUrl(summary?.cover_url ?? draft?.meta?.cover_url ?? "", { preset: AvatarPreset.IMAGE_500X650 }) ?? undefined;

const getCardText = ({ primary, fallback }: { primary?: string; fallback?: string }) => primary ?? fallback ?? "";

const getCardCount = ({ primary, fallback }: { primary?: number; fallback?: number }) => primary ?? fallback ?? 0;

const getCardMeterChips = ({ primary, fallback }: { primary?: RulebookMeterChip[]; fallback?: RulebookMeterChip[] }) => primary ?? fallback ?? [];

const getLocalDraftCardFields = ({
  draft,
  derivedFields,
  coverUrl,
}: {
  draft?: RulebookDraft;
  derivedFields: DerivedRulebookFields;
  coverUrl?: string;
}): RulebookListCardFields => ({
  name: draft?.definition.name ?? "",
  description: draft?.definition.description ?? "",
  ...(coverUrl ? { coverUrl } : {}),
  meterChips: derivedFields?.meter_chips ?? [],
  numHiddenMeters: derivedFields?.num_hidden_meters ?? 0,
  numRules: derivedFields?.num_rules ?? 0,
  numBots: 0,
  numPersonas: 0,
  status: RulebookListCardStatus.DRAFT,
});

const getSavedRulebookCardFields = ({
  summary,
  draft,
  derivedFields,
  coverUrl,
}: {
  summary: RulebookSummary;
  draft?: RulebookDraft;
  derivedFields: DerivedRulebookFields;
  coverUrl?: string;
}): RulebookListCardFields => ({
  name: getCardText({ primary: summary.name, fallback: draft?.definition.name }),
  description: getCardText({ primary: summary.description, fallback: draft?.definition.description }),
  ...(coverUrl ? { coverUrl } : {}),
  meterChips: getCardMeterChips({ primary: summary.meter_chips, fallback: derivedFields?.meter_chips }),
  numHiddenMeters: getCardCount({ primary: summary.num_hidden_meters, fallback: derivedFields?.num_hidden_meters }),
  numRules: getCardCount({ primary: summary.num_rules, fallback: derivedFields?.num_rules }),
  numBots: summary.num_attached_characters ?? 0,
  numPersonas: summary.num_attached_personas ?? 0,
  status: resolveRulebookListCardStatus({ summary }),
  ...(summary.moderation_fix ? { moderationFix: summary.moderation_fix } : {}),
});

const buildMyRulebookCardFields = ({ summary, draft }: { summary?: RulebookSummary; draft?: RulebookDraft }): RulebookListCardFields => {
  const derivedFields = getDraftRulebookFields(draft);
  const coverUrl = getRulebookCoverUrl({ summary, draft });

  if (!summary) {
    return getLocalDraftCardFields({ draft, derivedFields, coverUrl });
  }

  return getSavedRulebookCardFields({ summary, draft, derivedFields, coverUrl });
};

export default buildMyRulebookCardFields;
